import { useState, useEffect, useCallback } from "react";
import AdminLayout, { dispatchAdminSidebarUpdate } from "./AdminLayout";
import {
  listUsers, createUser, updateUser, deleteUser,
  enableUserAccount, disableUserAccount, resetUserPassword,
} from "../../api/services";
import {
  Search, Plus, Eye, Pencil, Lock, Unlock, KeyRound, Trash2,
  X, MoreHorizontal, ChevronLeft, ChevronRight, Save,
  Mail, Shield, Calendar, Clock, CheckCircle2, AlertCircle, Info,
  Users, TrendingUp, CheckSquare, Download, RefreshCw, Loader2,
} from "lucide-react";

/* ── Avatar ── */
const AV_COLORS = [
  ["#eef3fd","#009DB9"], ["#f0fdf4","#16a34a"], ["#eff6ff","#3b82f6"],
  ["#fff7ed","#ea580c"], ["#fdf4ff","#a21caf"], ["#f1f5f9","#475569"],
];
function avColor(name) { return AV_COLORS[(name?.charCodeAt(0) ?? 0) % AV_COLORS.length]; }
function initials(u)   { return `${u?.firstName?.[0] ?? ""}${u?.lastName?.[0] ?? ""}`.toUpperCase(); }

function Avatar({ user, size = 34 }) {
  const [bg, fg] = avColor(user.firstName);
  return (
    <div className="flex items-center justify-center flex-shrink-0 font-bold rounded-full"
      style={{ width: size, height: size, background: bg, color: fg, border: `1px solid ${fg}22`, fontSize: size * 0.38 }}>
      {initials(user)}
    </div>
  );
}

/* ── Badges ── */
function StatusBadge({ user }) {
  const isActive = user.active === true;
  const isDisabled = user.active === false;
  
  if (isActive) {
    return (
      <span className="inline-flex items-center gap-1.5 text-[11px] font-semibold rounded-full px-2.5 py-0.5"
        style={{ background: "#f0fdf4", color: "#16a34a" }}>
        <span className="w-1.5 h-1.5 rounded-full" style={{ background: "#16a34a" }} />
        Active
      </span>
    );
  } else if (isDisabled) {
    return (
      <span className="inline-flex items-center gap-1.5 text-[11px] font-semibold rounded-full px-2.5 py-0.5"
        style={{ background: "#fef2f2", color: "#dc2626" }}>
        <span className="w-1.5 h-1.5 rounded-full" style={{ background: "#dc2626" }} />
        Disabled
      </span>
    );
  }
  
  return (
    <span className="inline-flex items-center gap-1.5 text-[11px] font-semibold rounded-full px-2.5 py-0.5"
      style={{ background: "#fffbeb", color: "#d97706" }}>
      <span className="w-1.5 h-1.5 rounded-full" style={{ background: "#d97706" }} />
      Invited
    </span>
  );
}

function RoleBadge({ role }) {
  const map = { 
    SUPER_ADMIN: { bg: "#eef3fd", color: "#009DB9", label: "Super Admin" },
    ADMIN: { bg: "#e0f2fe", color: "#0284c7", label: "Admin" },
    MANAGER: { bg: "#ede9fe", color: "#7c3aed", label: "Manager" },
    SALES_REP: { bg: "#dcfce7", color: "#16a34a", label: "Sales Rep" },
  };
  const style = map[role] ?? map.SALES_REP;
  return (
    <span className="inline-flex items-center text-[11px] font-semibold rounded-full px-2.5 py-0.5"
      style={{ background: style.bg, color: style.color }}>
      {style.label}
    </span>
  );
}

/* ── Helpers ── */
const fmtDate   = (iso) => iso ? new Date(iso).toLocaleDateString("en-GB", { day: "numeric", month: "short" }) : "Never";
const fmtDT     = (iso) => iso ? new Date(iso).toLocaleString("en-GB", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" }) : "Never";
const fmtJoined = (iso) => iso ? new Date(iso).toLocaleDateString("en-GB", { day: "numeric", month: "long", year: "numeric" }) : "—";

/* ── Toast ── */
function useToast() {
  const [toasts, setToasts] = useState([]);
  const show = useCallback((msg, type = "success") => {
    const id = Date.now();
    setToasts(p => [...p, { id, msg, type }]);
    setTimeout(() => setToasts(p => p.filter(t => t.id !== id)), 3500);
  }, []);
  return { toasts, show };
}

function ToastStack({ toasts }) {
  const styles = {
    success: { bg: "#f0fdf4", border: "#bbf7d0", color: "#16a34a", icon: CheckCircle2 },
    error:   { bg: "#fef2f2", border: "#fca5a5", color: "#dc2626", icon: AlertCircle },
    warning: { bg: "#fffbeb", border: "#fde68a", color: "#d97706", icon: AlertCircle },
  };
  return (
    <div className="fixed bottom-6 right-6 z-[999] flex flex-col gap-2">
      {toasts.map(t => {
        const s = styles[t.type] || styles.success;
        const Icon = s.icon;
        return (
          <div key={t.id} className="flex items-center gap-2 px-4 py-2.5 rounded-xl text-[13px] font-medium min-w-[260px]"
            style={{ background: s.bg, border: `1px solid ${s.border}`, color: s.color, boxShadow: "0 4px 16px rgba(17,24,39,.1)" }}>
            <Icon size={14} strokeWidth={2} className="flex-shrink-0" />{t.msg}
          </div>
        );
      })}
    </div>
  );
}

/* ── Confirm Modal ── */
function ConfirmModal({ open, config, onClose, onConfirm, loading }) {
  if (!open || !config) return null;
  const styleMap = { 
    danger: { bg: "#dc2626", hover: "#b91c1c" }, 
    warning: { bg: "#d97706", hover: "#b45309" }, 
    success: { bg: "#16a34a", hover: "#15803d" }, 
    primary: { bg: "#009DB9", hover: "#007a90" } 
  };
  const s = styleMap[config.variant] || styleMap.primary;
  return (
    <div className="fixed inset-0 z-[500] flex items-center justify-center p-4"
      style={{ background: "rgba(17,24,39,.45)", backdropFilter: "blur(2px)" }}
      onClick={(e) => { if (e.target === e.currentTarget && !loading) onClose(); }}>
      <div className="flex flex-col w-full max-w-sm overflow-hidden bg-white rounded-2xl"
        style={{ boxShadow: "0 10px 40px rgba(17,24,39,.18)", border: "1px solid #eceef2" }}>
        <div className="flex items-start justify-between px-6 py-5 border-b border-neutral-100">
          <div className="text-[16px] font-bold text-neutral-900">{config.title}</div>
          <button onClick={onClose} disabled={loading} className="text-neutral-400 hover:text-neutral-600 disabled:opacity-40"><X size={16} /></button>
        </div>
        <div className="px-6 py-4">
          <p className="text-[13px] text-neutral-500 leading-relaxed">{config.message}</p>
        </div>
        <div className="flex justify-end gap-2 px-6 py-4 border-t border-neutral-100">
          <button onClick={onClose} disabled={loading}
            className="px-4 h-9 rounded-lg text-[13px] font-semibold border border-neutral-200 text-neutral-600 hover:bg-neutral-50 disabled:opacity-40">
            Cancel
          </button>
          <button onClick={onConfirm} disabled={loading}
            className="flex items-center gap-1.5 px-4 h-9 rounded-lg text-[13px] font-semibold text-white disabled:opacity-70"
            style={{ background: s.bg }}
            onMouseEnter={(e) => { if (!loading) e.currentTarget.style.background = s.hover; }}
            onMouseLeave={(e) => { e.currentTarget.style.background = s.bg; }}>
            {loading && <Loader2 size={13} className="animate-spin" />}
            {config.btnLabel}
          </button>
        </div>
      </div>
    </div>
  );
}

/* ── User Form Modal ── */
function UserFormModal({ open, onClose, onSubmit, user, submitting }) {
  const isEdit = !!user;
  const empty  = { firstName: "", lastName: "", email: "", role: "SALES_REP", title: "", phone: "" };
  const [form, setForm] = useState(empty);
  const set = (k, v) => setForm(p => ({ ...p, [k]: v }));

  useEffect(() => {
    setForm(user
      ? { 
          firstName: user.firstName ?? "", 
          lastName: user.lastName ?? "", 
          email: user.email ?? "", 
          role: user.role ?? "SALES_REP", 
          title: user.title ?? "", 
          phone: user.phone ?? "" 
        }
      : empty);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, open]);

  if (!open) return null;
  const canSubmit = form.firstName.trim() && form.email.trim() && !submitting;

  return (
    <div className="fixed inset-0 z-[500] flex items-center justify-center p-4"
      style={{ background: "rgba(17,24,39,.45)", backdropFilter: "blur(2px)" }}
      onClick={(e) => { if (e.target === e.currentTarget && !submitting) onClose(); }}>
      <div className="bg-white rounded-2xl w-full max-w-[520px] flex flex-col"
        style={{ maxHeight: "90vh", boxShadow: "0 10px 40px rgba(17,24,39,.18)", border: "1px solid #eceef2" }}>
        <div className="flex items-start justify-between flex-shrink-0 px-6 py-5 border-b border-neutral-100">
          <div>
            <div className="text-[16px] font-bold text-neutral-900">{isEdit ? "Edit User" : "Create New User"}</div>
            <div className="text-[12px] text-neutral-400 mt-0.5">
              {isEdit ? `${user.firstName} ${user.lastName} · ${user.email}` : "User will receive a welcome email with temporary credentials"}
            </div>
          </div>
          <button onClick={onClose} disabled={submitting} className="text-neutral-400 hover:text-neutral-600 disabled:opacity-40"><X size={16} /></button>
        </div>
        <div className="flex-1 px-6 py-5 overflow-y-auto">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3.5">
            <div className="flex flex-col gap-1">
              <label className="text-[11px] font-semibold text-neutral-500 uppercase tracking-wide">First Name <span className="text-red-500">*</span></label>
              <input value={form.firstName} onChange={(e) => set("firstName", e.target.value)} placeholder="Sarah"
                className="h-9 border border-neutral-200 rounded-lg px-3 text-[13px] outline-none focus:border-[#009DB9] bg-white" />
            </div>
            <div className="flex flex-col gap-1">
              <label className="text-[11px] font-semibold text-neutral-500 uppercase tracking-wide">Last Name</label>
              <input value={form.lastName} onChange={(e) => set("lastName", e.target.value)} placeholder="Wilson"
                className="h-9 border border-neutral-200 rounded-lg px-3 text-[13px] outline-none focus:border-[#009DB9] bg-white" />
            </div>
            <div className="flex flex-col col-span-1 gap-1 sm:col-span-2">
              <label className="text-[11px] font-semibold text-neutral-500 uppercase tracking-wide">Work Email <span className="text-red-500">*</span></label>
              <input value={form.email} onChange={(e) => set("email", e.target.value)} type="email" placeholder="user@company.com"
                disabled={isEdit}
                className="h-9 border border-neutral-200 rounded-lg px-3 text-[13px] outline-none focus:border-[#009DB9] bg-white disabled:opacity-50 disabled:cursor-not-allowed" />
            </div>
            <div className="flex flex-col gap-1">
              <label className="text-[11px] font-semibold text-neutral-500 uppercase tracking-wide">Role</label>
              <select value={form.role} onChange={(e) => set("role", e.target.value)}
                className="h-9 border border-neutral-200 rounded-lg px-2.5 text-[13px] outline-none focus:border-[#009DB9] bg-white cursor-pointer">
                <option value="SUPER_ADMIN">Super Admin</option>
                <option value="ADMIN">Admin</option>
                <option value="MANAGER">Manager</option>
                <option value="SALES_REP">Sales Rep</option>
              </select>
            </div>
            <div className="flex flex-col gap-1">
              <label className="text-[11px] font-semibold text-neutral-500 uppercase tracking-wide">Job Title</label>
              <input value={form.title} onChange={(e) => set("title", e.target.value)} placeholder="Sales Manager"
                className="h-9 border border-neutral-200 rounded-lg px-3 text-[13px] outline-none focus:border-[#009DB9] bg-white" />
            </div>
            {!isEdit && (
              <div className="flex flex-col col-span-1 gap-1 sm:col-span-2">
                <label className="text-[11px] font-semibold text-neutral-500 uppercase tracking-wide">Phone</label>
                <input value={form.phone} onChange={(e) => set("phone", e.target.value)} type="tel"
                  className="h-9 border border-neutral-200 rounded-lg px-3 text-[13px] outline-none focus:border-[#009DB9] bg-white" />
              </div>
            )}
          </div>
          {!isEdit && (
            <div className="flex items-start gap-2 px-3 py-3 mt-4 rounded-lg" style={{ background: "#e0f2fe", border: "1px solid #b3e0f0" }}>
              <Info size={13} className="flex-shrink-0 mt-0.5" style={{ color: "#009DB9" }} />
              <p className="text-[12px]" style={{ color: "#009DB9" }}>A secure temporary password will be generated and emailed to the user.</p>
            </div>
          )}
        </div>
        <div className="flex justify-end flex-shrink-0 gap-2 px-6 py-4 border-t border-neutral-100">
          <button onClick={onClose} disabled={submitting}
            className="px-4 h-9 rounded-lg text-[13px] font-semibold border border-neutral-200 text-neutral-600 hover:bg-neutral-50 disabled:opacity-40">
            Cancel
          </button>
          <button onClick={() => onSubmit(form)} disabled={!canSubmit}
            className="flex items-center gap-1.5 px-4 h-9 rounded-lg text-[13px] font-semibold text-white disabled:opacity-40"
            style={{ background: "#009DB9" }}
            onMouseEnter={(e) => { if (canSubmit) e.currentTarget.style.background = "#007a90"; }}
            onMouseLeave={(e) => { e.currentTarget.style.background = "#009DB9"; }}>
            {submitting && <Loader2 size={13} className="animate-spin" />}
            <Save size={13} />{isEdit ? "Save Changes" : "Create User"}
          </button>
        </div>
      </div>
    </div>
  );
}

/* ── User Drawer ── */
function UserDrawer({ open, user, onClose, onEdit, onAction }) {
  if (!user) return null;
  return (
    <div className={`fixed inset-0 z-[400] ${open ? "pointer-events-auto" : "pointer-events-none"}`}>
      <div className="absolute inset-0 transition-opacity duration-200"
        style={{ background: "rgba(17,24,39,.3)", backdropFilter: "blur(1px)", opacity: open ? 1 : 0 }}
        onClick={onClose} />
      <div className="absolute top-0 bottom-0 right-0 flex flex-col overflow-y-auto transition-transform duration-200 bg-white"
        style={{ width: 420, maxWidth: "100vw", boxShadow: "-4px 0 40px rgba(17,24,39,.15)", transform: open ? "translateX(0)" : "translateX(100%)" }}>

        <div className="flex items-start gap-3 px-6 py-5 border-b border-neutral-100">
          <Avatar user={user} size={44} />
          <div className="flex-1 min-w-0">
            <div className="text-[16px] font-bold text-neutral-900">{user.firstName} {user.lastName}</div>
            <div className="text-[12px] text-neutral-400 mt-0.5">{user.email}</div>
            <div className="flex items-center gap-1.5 mt-2 flex-wrap">
              <RoleBadge role={user.role} />
              <StatusBadge user={user} />
            </div>
          </div>
          <button onClick={onClose} className="flex-shrink-0 text-neutral-400 hover:text-neutral-600"><X size={18} /></button>
        </div>

        <div className="flex items-center gap-2 px-6 py-3.5 border-b border-neutral-100 flex-wrap">
          <button onClick={() => onEdit(user)}
            className="flex items-center gap-1.5 px-3 h-8 rounded-lg text-[12px] font-semibold border border-neutral-200 text-neutral-700 hover:bg-neutral-50">
            <Pencil size={12} /> Edit
          </button>
          <button onClick={() => onAction("reset", user)}
            className="flex items-center gap-1.5 px-3 h-8 rounded-lg text-[12px] font-semibold"
            style={{ background: "#fffbeb", color: "#d97706", border: "1px solid #fde68a" }}>
            <KeyRound size={12} /> Reset Password
          </button>
          {user.active === true
            ? <button onClick={() => onAction("disable", user)}
                className="flex items-center gap-1.5 px-3 h-8 rounded-lg text-[12px] font-semibold text-red-600 hover:bg-red-50">
                <Lock size={12} /> Disable
              </button>
            : <button onClick={() => onAction("enable", user)}
                className="flex items-center gap-1.5 px-3 h-8 rounded-lg text-[12px] font-semibold"
                style={{ background: "#f0fdf4", color: "#16a34a", border: "1px solid #bbf7d0" }}>
                <Unlock size={12} /> Enable
              </button>
          }
          <button onClick={() => onAction("delete", user)}
            className="flex items-center gap-1.5 px-3 h-8 rounded-lg text-[12px] font-semibold text-red-600 hover:bg-red-50 ml-auto">
            <Trash2 size={12} /> Delete
          </button>
        </div>

        <div className="px-6 py-5 border-b border-neutral-100">
          <div className="text-[12px] font-semibold text-neutral-400 uppercase tracking-[.07em] mb-3">Performance</div>
          <div className="grid grid-cols-3 gap-2.5">
            {[
              { icon: Users,       label: "Contacts", value: user.totalContacts ?? 0 },
              { icon: TrendingUp,  label: "Deals",    value: user.totalDeals ?? 0 },
              { icon: CheckSquare, label: "Tasks",    value: user.totalTasks ?? 0 },
            ].map(({ label, value }) => (
              <div key={label} className="px-2 py-3 text-center border rounded-xl border-neutral-100 bg-neutral-50">
                <div className="text-[22px] font-bold text-neutral-900 tabular-nums leading-none">{value}</div>
                <div className="text-[11px] text-neutral-400 mt-1.5">{label}</div>
              </div>
            ))}
          </div>
        </div>

        <div className="px-6 py-5">
          <div className="text-[12px] font-semibold text-neutral-400 uppercase tracking-[.07em] mb-3">Account Details</div>
          {[
            { icon: Mail,     label: "Email",      value: user.email },
            { icon: Shield,   label: "Role",       value: user.role === "SUPER_ADMIN" ? "Super Admin" : user.role === "ADMIN" ? "Admin" : user.role === "MANAGER" ? "Manager" : "Sales Rep" },
            { icon: Calendar, label: "Joined",     value: fmtJoined(user.createdAt) },
            { icon: Clock,    label: "Last Login", value: fmtDT(user.lastLogin) },
          ].map(({ icon: Icon, label, value }) => (
            <div key={label} className="flex items-center gap-3 py-2.5 border-b border-neutral-100 last:border-0">
              <Icon size={12} className="flex-shrink-0 text-neutral-300" strokeWidth={2} />
              <span className="text-[12px] text-neutral-400 w-20 flex-shrink-0">{label}</span>
              <span className="text-[12px] font-medium text-neutral-700">{value}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

/* ── Row menu ── */
function RowMenu({ user, onView, onEdit, onAction }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="relative">
      <button onClick={(e) => { e.stopPropagation(); setOpen(p => !p); }}
        className="flex items-center justify-center rounded-lg w-7 h-7 text-neutral-400 hover:bg-neutral-100">
        <MoreHorizontal size={15} />
      </button>
      {open && (
        <>
          <div className="fixed inset-0 z-[190]" onClick={() => setOpen(false)} />
          <div className="absolute right-0 top-full mt-1 bg-white rounded-xl border border-neutral-100 z-[200] min-w-[185px] overflow-hidden"
            style={{ boxShadow: "0 4px 16px rgba(17,24,39,.1)" }}>
            {[
              { icon: Eye,    label: "View details",                                  fn: () => { onView(user); setOpen(false); } },
              { icon: Pencil, label: "Edit",                                           fn: () => { onEdit(user); setOpen(false); } },
              { icon: user.active === true ? Lock : Unlock,
                label: user.active === true ? "Disable account" : "Enable account",
                fn: () => { onAction(user.active === true ? "disable" : "enable", user); setOpen(false); } },
              { icon: KeyRound, label: "Reset password", fn: () => { onAction("reset",  user); setOpen(false); } },
              { icon: Trash2,   label: "Delete user",    fn: () => { onAction("delete", user); setOpen(false); }, danger: true },
            ].map(({ icon: Icon, label, fn, danger }) => (
              <button key={label} onClick={fn}
                className="flex items-center gap-2 w-full px-3.5 py-2.5 text-[12px] font-medium hover:bg-neutral-50"
                style={{ color: danger ? "#dc2626" : "#374151" }}>
                <Icon size={12} style={{ color: danger ? "#dc2626" : "#9ca3af" }} />{label}
              </button>
            ))}
          </div>
        </>
      )}
    </div>
  );
}

/* ── Confirm config ── */
const CONFIRMS = {
  delete:  { title: "Delete user?",     message: "This will permanently delete the account and all associated data. This cannot be undone.", variant: "danger",  btnLabel: "Delete user" },
  disable: { title: "Disable account?", message: "The user will lose access immediately. You can re-enable at any time.",                    variant: "danger",  btnLabel: "Disable"    },
  enable:  { title: "Enable account?",  message: "The user will regain full access to the workspace.",                                    variant: "success", btnLabel: "Enable"     },
  reset:   { title: "Reset password?",  message: "A secure temporary password will be generated and emailed to the user.",                variant: "primary", btnLabel: "Send reset" },
};

const CLIENT_PAGE = 6;

/* ══════════════════════ MAIN PAGE ══════════════════════ */
export default function AdminUsers() {
  const { toasts, show: showToast } = useToast();

  const [users,     setUsers]     = useState([]);
  const [loading,   setLoading]   = useState(true);
  const [error,     setError]     = useState(null);
  const [search,    setSearch]    = useState("");
  const [roleF,     setRoleF]     = useState("All");
  const [statusF,   setStatusF]   = useState("All");
  const [uiPage,    setUiPage]    = useState(0);

  const [drawer,    setDrawer]    = useState({ open: false, user: null });
  const [form,      setForm]      = useState({ open: false, user: null });
  const [confirm,   setConfirm]   = useState({ open: false, type: null, user: null });
  const [actionBusy, setActionBusy] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  /* Load users */
  useEffect(() => {
    const load = async () => {
      setLoading(true);
      setError(null);
      try {
        const data = await listUsers({ page: 0, size: 100 });
        const userList = Array.isArray(data) ? data : (data.content ?? []);
        setUsers(userList);
        // Sync admin sidebar badge on initial load
        dispatchAdminSidebarUpdate({ users: userList.length });
      } catch (err) {
        console.error("Failed to load users:", err);
        setError(err.response?.data?.message || "Failed to load users");
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  /* Client-side filter */
  const filtered = users.filter(u => {
    const matchQ = !search || `${u.firstName} ${u.lastName} ${u.email}`.toLowerCase().includes(search.toLowerCase());
    const matchR = roleF   === "All" || u.role   === roleF;
    const matchS = statusF === "All" || (statusF === "active" ? u.active === true : statusF === "disabled" ? u.active === false : true);
    return matchQ && matchR && matchS;
  });
  const totalPages = Math.max(1, Math.ceil(filtered.length / CLIENT_PAGE));
  const paged      = filtered.slice(uiPage * CLIENT_PAGE, (uiPage + 1) * CLIENT_PAGE);

  useEffect(() => setUiPage(0), [search, roleF, statusF]);

  /* Confirm action */
  const handleAction = (type, user) => setConfirm({ open: true, type, user });

  const execAction = async () => {
    const { type, user } = confirm;
    setActionBusy(true);
    try {
      let updatedUsers = users;

      if (type === "delete") {
        await deleteUser(user.id);
        updatedUsers = users.filter(u => u.id !== user.id);
        setUsers(updatedUsers);
        if (drawer.user?.id === user.id) setDrawer({ open: false, user: null });
      }
      if (type === "disable") {
        await disableUserAccount(user.id);
        updatedUsers = users.map(u => u.id === user.id ? { ...u, active: false } : u);
        setUsers(updatedUsers);
      }
      if (type === "enable") {
        await enableUserAccount(user.id);
        updatedUsers = users.map(u => u.id === user.id ? { ...u, active: true } : u);
        setUsers(updatedUsers);
      }
      if (type === "reset") {
        await resetUserPassword(user.id);
        // No user count change for password reset
      }

      // Dispatch updated user count to admin sidebar badge immediately
      // (reset doesn't change the count, skip for that)
      if (type !== "reset") {
        dispatchAdminSidebarUpdate({ users: updatedUsers.length });
      }

      const msgs = {
        delete:  ["User deleted",             "error"  ],
        disable: ["Account disabled",         "warning"],
        enable:  ["Account enabled",          "success"],
        reset:   ["Password reset email sent","success"],
      };
      showToast(...msgs[type]);
      setConfirm({ open: false, type: null, user: null });
    } catch (err) {
      showToast(err.response?.data?.message || "Action failed", "error");
    } finally {
      setActionBusy(false);
    }
  };

  /* Form submit */
  const handleFormSubmit = async (data) => {
    setSubmitting(true);
    try {
      if (form.user) {
        // Edit — user count does not change
        const updated = await updateUser(form.user.id, data);
        setUsers(p => p.map(u => u.id === form.user.id ? { ...u, ...updated } : u));
        showToast("User updated successfully", "success");
      } else {
        // Create — user count increases by 1
        const created = await createUser(data);
        const newUser = {
          totalContacts: 0,
          totalDeals: 0,
          totalTasks: 0,
          active: true,
          createdAt: new Date().toISOString(),
          lastLogin: null,
          ...data,
          ...created,
        };
        const updatedUsers = [newUser, ...users];
        setUsers(updatedUsers);
        // Dispatch updated user count to admin sidebar badge immediately
        dispatchAdminSidebarUpdate({ users: updatedUsers.length });
        showToast("User created — welcome email sent", "success");
      }
      setForm({ open: false, user: null });
    } catch (err) {
      showToast(err.response?.data?.message || "Failed to save user", "error");
    } finally {
      setSubmitting(false);
    }
  };

  const drawerUser = drawer.user ? users.find(u => u.id === drawer.user.id) ?? drawer.user : null;

  if (error) return (
    <AdminLayout title="Users" subtitle="Manage team members">
      <div className="flex flex-col items-center justify-center gap-3 py-20">
        <AlertCircle size={32} className="text-red-400" strokeWidth={1.5} />
        <p className="text-[14px] font-semibold text-neutral-700">Failed to load users</p>
        <p className="text-[12px] text-neutral-400">{error}</p>
        <button onClick={() => window.location.reload()}
          className="flex items-center gap-1.5 mt-2 px-4 h-9 rounded-lg text-[13px] font-semibold border border-neutral-200 hover:bg-neutral-50">
          <RefreshCw size={13} /> Retry
        </button>
      </div>
    </AdminLayout>
  );

  return (
    <AdminLayout title="Users" subtitle={`${users.length} total · ${users.filter(u => u.active === true).length} active`}>

      {/* Header actions */}
      <div className="flex flex-wrap justify-end gap-2 mb-4">
        <div className="px-3 py-1.5 bg-white border border-neutral-100 rounded-lg text-[12px] text-neutral-400 flex items-center gap-1.5">
          <span className="w-1.5 h-1.5 rounded-full bg-green-500" />System online
        </div>
        <button onClick={() => showToast("Export started", "success")}
          className="flex items-center gap-1.5 px-3.5 h-9 rounded-lg text-[13px] font-semibold border border-neutral-200 text-neutral-700 bg-white hover:bg-neutral-50">
          <Download size={13} /> Export
        </button>
        <button onClick={() => setForm({ open: true, user: null })}
          className="flex items-center gap-1.5 px-3.5 h-9 rounded-lg text-[13px] font-semibold text-white"
          style={{ background: "#009DB9" }}
          onMouseEnter={(e) => (e.currentTarget.style.background = "#007a90")}
          onMouseLeave={(e) => (e.currentTarget.style.background = "#009DB9")}>
          <Plus size={13} /> New User
        </button>
      </div>

      {/* Table */}
      <div className="overflow-hidden bg-white border border-neutral-100 rounded-xl" style={{ boxShadow: "0 1px 3px rgba(17,24,39,.08)" }}>

        {/* Toolbar */}
        <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3 px-5 py-3.5 border-b border-neutral-100">
          <div className="relative flex-1 min-w-[200px]">
            <Search size={13} className="absolute -translate-y-1/2 pointer-events-none left-3 top-1/2 text-neutral-300" />
            <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search users..."
              className="w-full h-9 pl-8 pr-3 border border-neutral-200 rounded-lg text-[13px] outline-none focus:border-[#009DB9] bg-white" />
          </div>
          <div className="flex gap-2">
            <select value={roleF} onChange={(e) => setRoleF(e.target.value)}
              className="flex-1 sm:flex-initial h-9 px-2.5 border border-neutral-200 rounded-lg text-[12px] text-neutral-700 outline-none focus:border-[#009DB9] bg-white cursor-pointer">
              <option value="All">All Roles</option>
              <option value="SUPER_ADMIN">Super Admin</option>
              <option value="ADMIN">Admin</option>
              <option value="MANAGER">Manager</option>
              <option value="SALES_REP">Sales Rep</option>
            </select>
            <select value={statusF} onChange={(e) => setStatusF(e.target.value)}
              className="flex-1 sm:flex-initial h-9 px-2.5 border border-neutral-200 rounded-lg text-[12px] text-neutral-700 outline-none focus:border-[#009DB9] bg-white cursor-pointer">
              <option value="All">All Status</option>
              <option value="active">Active</option>
              <option value="disabled">Disabled</option>
            </select>
            <button onClick={() => window.location.reload()}
              className="flex items-center justify-center bg-white border rounded-lg w-9 h-9 text-neutral-400 border-neutral-200 hover:bg-neutral-50">
              <RefreshCw size={14} className={loading ? "animate-spin" : ""} />
            </button>
          </div>
        </div>

        {/* Column headers - hidden on mobile */}
        <div className="hidden md:grid items-center gap-3 px-5 py-2.5 border-b border-neutral-100 bg-neutral-50"
          style={{ gridTemplateColumns: "2fr 1fr 1fr 1fr 120px 36px" }}>
          {["User","Role","Status","Last Login","Activity",""].map(h => (
            <div key={h} className="text-[11px] font-semibold text-neutral-400 uppercase tracking-[.07em]">{h}</div>
          ))}
        </div>

        {/* Rows */}
        {loading
          ? Array.from({ length: 6 }).map((_, i) => (
              <div key={i} className="block p-4 border-b md:hidden border-neutral-100">
                <div className="flex items-center gap-3">
                  <div className="flex-shrink-0 w-10 h-10 rounded-full animate-pulse bg-neutral-100" />
                  <div className="flex-1">
                    <div className="w-3/4 h-4 rounded bg-neutral-100 animate-pulse" />
                    <div className="w-1/2 h-3 mt-2 rounded bg-neutral-100 animate-pulse" />
                  </div>
                </div>
              </div>
            ))
          : paged.length === 0
          ? <div className="py-14 text-center text-[13px] text-neutral-400">No users match your filters.</div>
          : paged.map(user => (
              <>
                {/* Mobile Card View */}
                <div key={`${user.id}-mobile`} className="block p-4 transition-colors border-b cursor-pointer md:hidden border-neutral-100 last:border-0 hover:bg-blue-50/30"
                  onClick={() => setDrawer({ open: true, user })}>
                  <div className="flex items-start gap-3">
                    <Avatar user={user} size={40} />
                    <div className="flex-1 min-w-0">
                      <div className="flex items-start justify-between gap-2">
                        <div>
                          <div className="text-[14px] font-semibold text-neutral-900">{user.firstName} {user.lastName}</div>
                          <div className="text-[11px] text-neutral-400 mt-0.5 break-all">{user.email}</div>
                        </div>
                        <div onClick={(e) => e.stopPropagation()}>
                          <RowMenu user={user}
                            onView={(u) => setDrawer({ open: true, user: u })}
                            onEdit={(u) => setForm({ open: true, user: u })}
                            onAction={handleAction} />
                        </div>
                      </div>
                      <div className="flex flex-wrap items-center gap-2 mt-2">
                        <RoleBadge role={user.role} />
                        <StatusBadge user={user} />
                      </div>
                      <div className="flex items-center gap-3 mt-3 text-[11px] text-neutral-400">
                        <span>Last login: {fmtDate(user.lastLogin)}</span>
                        <span><strong className="text-neutral-700">{user.totalContacts ?? 0}</strong> contacts</span>
                        <span><strong className="text-neutral-700">{user.totalDeals ?? 0}</strong> deals</span>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Desktop Table Row */}
                <div key={user.id}
                  className="hidden md:grid items-center gap-3 px-5 py-3.5 border-b border-neutral-100 last:border-0 hover:bg-blue-50/30 transition-colors cursor-pointer"
                  style={{ gridTemplateColumns: "2fr 1fr 1fr 1fr 120px 36px" }}
                  onClick={() => setDrawer({ open: true, user })}>
                  <div className="flex items-center gap-2.5">
                    <Avatar user={user} size={34} />
                    <div>
                      <div className="text-[13px] font-semibold text-neutral-900">{user.firstName} {user.lastName}</div>
                      <div className="text-[11px] text-neutral-400">{user.email}</div>
                    </div>
                  </div>
                  <div><RoleBadge role={user.role} /></div>
                  <div><StatusBadge user={user} /></div>
                  <div className="text-[12px] text-neutral-500">{fmtDate(user.lastLogin)}</div>
                  <div className="flex items-center gap-2">
                    <span className="text-[11px] text-neutral-400"><strong className="text-neutral-700">{user.totalContacts ?? 0}</strong> cts</span>
                    <span className="text-[11px] text-neutral-400"><strong className="text-neutral-700">{user.totalDeals ?? 0}</strong> deals</span>
                  </div>
                  <div onClick={(e) => e.stopPropagation()}>
                    <RowMenu user={user}
                      onView={(u) => setDrawer({ open: true, user: u })}
                      onEdit={(u) => setForm({ open: true, user: u })}
                      onAction={handleAction} />
                  </div>
                </div>
              </>
            ))
        }

        {/* Pagination */}
        <div className="flex flex-col items-center justify-between gap-3 px-5 py-3 border-t sm:flex-row border-neutral-100">
          <span className="text-[12px] text-neutral-400 order-2 sm:order-1">
            {loading ? "Loading…" : `Showing ${uiPage * CLIENT_PAGE + 1}–${Math.min((uiPage + 1) * CLIENT_PAGE, filtered.length)} of ${filtered.length}`}
          </span>
          <div className="flex items-center order-1 gap-1 sm:order-2">
            <button onClick={() => setUiPage(p => Math.max(0, p - 1))} disabled={uiPage === 0 || loading}
              className="flex items-center justify-center rounded-lg w-7 h-7 text-neutral-400 hover:bg-neutral-100 disabled:opacity-30">
              <ChevronLeft size={14} />
            </button>
            {Array.from({ length: Math.min(totalPages, 5) }, (_, i) => (
              <button key={i} onClick={() => setUiPage(i)}
                className="w-7 h-7 rounded-lg text-[12px] font-medium"
                style={uiPage === i ? { background: "#009DB9", color: "#fff" } : { color: "#737373" }}>
                {i + 1}
              </button>
            ))}
            <button onClick={() => setUiPage(p => Math.min(totalPages - 1, p + 1))} disabled={uiPage >= totalPages - 1 || loading}
              className="flex items-center justify-center rounded-lg w-7 h-7 text-neutral-400 hover:bg-neutral-100 disabled:opacity-30">
              <ChevronRight size={14} />
            </button>
          </div>
        </div>
      </div>

      <UserDrawer open={drawer.open} user={drawerUser}
        onClose={() => setDrawer({ open: false, user: null })}
        onEdit={(u) => { setForm({ open: true, user: u }); setDrawer({ open: false, user: null }); }}
        onAction={handleAction} />

      <UserFormModal open={form.open} user={form.user} submitting={submitting}
        onClose={() => !submitting && setForm({ open: false, user: null })}
        onSubmit={handleFormSubmit} />

      <ConfirmModal open={confirm.open} loading={actionBusy}
        config={confirm.type ? { ...CONFIRMS[confirm.type], message: CONFIRMS[confirm.type].message + (confirm.user ? ` Account: ${confirm.user.firstName} ${confirm.user.lastName}.` : "") } : null}
        onClose={() => !actionBusy && setConfirm({ open: false, type: null, user: null })}
        onConfirm={execAction} />

      <ToastStack toasts={toasts} />
    </AdminLayout>
  );
}